import { useState, useEffect, useRef, useLayoutEffect, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { Paper, Tooltip } from "@mui/material";
import {
  Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  deleteInvoice,
  updateInvoiceStatus,
  approveInvoice,
  declineInvoice,
  markInvoicePaid,
  setInvoiceEnteredAcct,
  bulkMarkInvoicesPaid,
  bulkApproveInvoices,
  getInvoiceSignedUrls,
  INVOICE_STATUSES,
  statusStyle,
  formatCurrency,
  formatInvoiceNo,
  itemLabel,
  sortInvoiceItems,
  isInvoiceDeletable,
  isInvoiceEditable,
  resolveRateType,
  flatAmountOf,
} from "./Api";
import { InvoiceFormModal } from "./InvoiceFormModal";
import { readInvoiceDraft, discardInvoiceDraft } from "./invoiceDraft";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { InvoiceForwardModal } from "./InvoiceForwardModal";
import { PayrollCalendarModal } from "./PayrollCalendarModal";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const STATUS_VALUES = INVOICE_STATUSES.map((s) => s.value);

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const payable = (inv) => (inv?.total != null ? inv.total : inv?.amount);

// One invoice's identity in this list. Every line of a submission shares its
// groupId; a pre-batching row has none and stands alone under its own _id —
// keying those on the number would merge two unrelated legacy records that
// happen to share one.
const groupKeyOf = (inv) => inv?.groupId || `single-${inv?._id}`;

// Derived overdue: an unpaid (not declined) invoice whose payment date has arrived.
const isOverdue = (inv) => {
  if (!inv?.dueDate || inv.status === "Paid" || inv.status === "Declined") return false;
  return moment.utc(inv.dueDate).startOf("day").valueOf() <= moment().startOf("day").valueOf();
};

const OverdueChip = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
    borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
  }}>
    <FeatherIcon icon="alert-triangle" size={10} /> Overdue
  </span>
);

const SyncedScroll = ({ children, maxHeight }) => {
  const contentRef = useRef(null);
  const [cursor, setCursor] = useState("grab");
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });

  useEffect(() => {
    const onMove = (e) => {
      if (!drag.current.active) return;
      const el = contentRef.current;
      if (!el) return;
      const dist = e.pageX - drag.current.startX;
      if (Math.abs(dist) > 4) {
        drag.current.moved = true;
        el.scrollLeft = drag.current.scrollLeft - dist;
      }
    };
    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      setCursor("grab");
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, a, input, select, label, [role='button']")) return;
    const el = contentRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
    setCursor("grabbing");
    setDragging(true);
  };

  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const showHandle = hovered && !dragging;

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={contentRef}
        onMouseDown={onMouseDown}
        onClickCapture={onClickCapture}
        style={{
          overflowX: "auto",
          overflowY: maxHeight ? "auto" : "visible",
          maxHeight: maxHeight || "none",
          cursor,
          userSelect: "none",
        }}
      >
        {children}
      </div>

      {/* Floating drag handle — appears on hover, hides while dragging */}
      <div style={{
        position: "absolute",
        bottom: 14,
        left: "50%",
        transform: `translateX(-50%) translateY(${showHandle ? 0 : 6}px)`,
        opacity: showHandle ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity 0.18s ease, transform 0.18s ease",
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "rgba(15,23,42,0.78)",
        backdropFilter: "blur(6px)",
        color: "#fff",
        borderRadius: 20,
        padding: "5px 14px",
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 16px rgba(15,23,42,0.22)",
        letterSpacing: "0.2px",
      }}>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <path d="M1 5h12M1 5l3-3M1 5l3 3M13 5l-3-3M13 5l-3 3"
            stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Hold &amp; drag to scroll
      </div>
    </div>
  );
};

const StyledCheckbox = ({ checked, onChange, disabled, label }) => (
  <label style={{ display: "inline-flex", alignItems: "center", cursor: disabled ? "not-allowed" : "pointer", margin: 0 }} aria-label={label}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
      aria-label={label}
    />
    <span style={{
      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: checked ? "2px solid #4F46E5" : "2px solid #CBD5E1",
      background: checked ? "#4F46E5" : "#fff",
      transition: "all 0.12s ease",
      opacity: disabled ? 0.4 : 1,
      boxShadow: checked ? "0 0 0 3px rgba(79,70,229,0.15)" : "none",
    }}>
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  </label>
);

const StatusBadge = ({ status }) => {
  const cfg = statusStyle(status);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem",
        fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const MENU_LABEL = {
  padding: "7px 12px 3px",
  fontSize: "0.62rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#94A3B8",
};

/**
 * One control for everything you can do to an invoice's status.
 *
 * The status cell used to stack up to five separate controls — a status
 * dropdown, an overdue chip, Approve, Decline, and Mark entered — which is what
 * made the rows so tall and put a destructive action (Decline) at the same
 * visual weight as a label.
 *
 * Now the pill IS the control. Overdue and entered-in-accounting stay ON the
 * trigger rather than moving into the menu, because neither is an action and
 * both are things you must see without clicking: overdue is urgent, and entered
 * tells you the invoice is already in the books.
 *
 * Actions are computed from role AND current status, so the menu only ever
 * offers a transition the server would accept.
 *
 * The two halves of this menu have different blast radius, and the menu says
 * so. Approve / Decline / Mark Paid move the WHOLE invoice — the server walks
 * every line of the group. "Set status" is the manual override and moves the
 * one line it was opened on: `PATCH /:id/status` transitions a single document
 * and emails on that document alone, so a client that fanned it out over the
 * group would send one notification per line — exactly the fan-out the batching
 * design forbids. Rather than pretend, the list labels it as per-item wherever
 * the invoice has more than one.
 */
const StatusControl = ({
  invoice,
  canChangeStatus,
  canApprove,
  canMarkPaid,
  canSetEntered,
  overdue,
  busy,
  siblingCount = 0,
  partialGroup = false,
  onStatusChange,
  onApprove,
  onDecline,
  onMarkPaid,
  onToggleEntered,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const cfg = statusStyle(invoice.status);
  const entered = !!invoice.enteredAcctAt;

  const isPending = invoice.status === "Pending";
  const isApproved = invoice.status === "Approved";
  const actions = [];
  if (canApprove && isPending) {
    actions.push({ key: "approve", label: "Approve", icon: "check", color: "#059669", run: () => onApprove(invoice) });
    actions.push({ key: "decline", label: "Decline", icon: "x", color: "#DC2626", run: () => onDecline(invoice) });
  }
  if (canMarkPaid && isApproved) {
    actions.push({ key: "paid", label: "Mark Paid", icon: "dollar-sign", color: "#059669", run: () => onMarkPaid(invoice) });
  }

  // Nothing to open: show the plain badge rather than a dropdown that would
  // reveal an empty menu.
  const hasMenu = actions.length > 0 || canSetEntered || canChangeStatus;

  const positionMenu = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight || 200;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + 12 && r.top > spaceBelow;
    setCoords({
      top: openUp ? Math.max(8, r.top - menuH - 6) : r.bottom + 6,
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (open) positionMenu();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  // An invoice with a single line has no "rest of the invoice" to mislead
  // anyone about, so it keeps the plain wording.
  const multiItem = siblingCount > 0 || partialGroup;
  const thisItem = itemLabel(invoice) || formatInvoiceNo(invoice);

  const choose = async (newStatus) => {
    setOpen(false);
    if (newStatus === invoice.status) return;

    const others = partialGroup || siblingCount !== 1
      ? "The invoice's other items keep their current status."
      : "The invoice's other item keeps its current status.";

    const res = await Swal.fire({
      title: multiItem ? `Change ${thisItem} to "${newStatus}"?` : `Change status to "${newStatus}"?`,
      html: multiItem
        ? `<p>This changes <strong>${thisItem}</strong> only. ${others}<br/><small style="color:#64748B">Approve, Decline and Mark Paid act on the whole invoice.</small></p>`
        : "<p>Are you sure?</p>",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;

    setLoading(true);
    try {
      const result = await updateInvoiceStatus(invoice._id, { status: newStatus });
      onStatusChange(invoice._id, result?.data || { ...invoice, status: newStatus });
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  // Markers that ride on the pill instead of taking a row of their own.
  const markers = (
    <>
      {overdue && (
        <Tooltip title="Past its due date" arrow>
          <span style={{ display: "inline-flex" }}>
            <FeatherIcon icon="alert-triangle" size={11} color="#B91C1C" />
          </span>
        </Tooltip>
      )}
      {entered && (
        <Tooltip title="Entered in accounting" arrow>
          <span style={{ display: "inline-flex" }}>
            <FeatherIcon icon="book-open" size={11} color="#4338CA" />
          </span>
        </Tooltip>
      )}
    </>
  );

  const pill = {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: cfg.bg, color: cfg.color,
    // Overdue re-colours the border rather than adding another chip, so the row
    // keeps one control while the warning stays impossible to miss.
    border: overdue ? "1px solid #FCA5A5" : `1px solid ${cfg.border}`,
    boxShadow: overdue ? "0 0 0 3px rgba(220,38,38,0.12)" : "none",
    borderRadius: 20, padding: "4px 10px", fontSize: "0.73rem", fontWeight: 700,
    minWidth: 116, justifyContent: "space-between",
  };

  if (!hasMenu) {
    return (
      <span style={{ ...pill, cursor: "default" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
          {invoice.status}
        </span>
        {markers}
      </span>
    );
  }

  return (
    <div style={{ display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        disabled={loading || busy}
        onClick={() => setOpen((o) => !o)}
        className="iv-status-trigger"
        style={{
          ...pill,
          cursor: loading || busy ? "not-allowed" : "pointer",
          opacity: loading || busy ? 0.7 : 1,
          transition: "box-shadow 0.12s, transform 0.12s",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
          {invoice.status}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          {markers}
          {loading || busy ? (
            <Spinner size="sm" style={{ color: cfg.color, width: 11, height: 11 }} />
          ) : (
            <svg width="9" height="5" viewBox="0 0 9 5" fill="none" style={{ transition: "transform 0.12s", transform: open ? "rotate(180deg)" : "none" }}>
              <path d="M1 1L4.5 4L8 1" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="iv-status-menu"
          style={{
            position: "fixed", top: coords.top, left: coords.left,
            minWidth: Math.max(coords.width, 186), zIndex: 13000,
          }}
        >
          {/* Actions first: this is what someone opening the menu came to do.
              Only the transitions valid for THIS user at THIS stage appear, so
              the menu never offers something that would be refused. */}
          {actions.length > 0 && (
            <>
              <div style={MENU_LABEL}>Actions</div>
              {actions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className="iv-status-option"
                  disabled={busy}
                  onClick={() => { setOpen(false); a.run(); }}
                  style={{ background: "#fff", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
                >
                  <FeatherIcon icon={a.icon} size={13} color={a.color} />
                  <span style={{ color: a.color, fontWeight: 700, fontSize: "0.78rem", flex: 1, textAlign: "left" }}>
                    {a.label}
                  </span>
                </button>
              ))}
            </>
          )}

          {canSetEntered && (
            <>
              <div style={MENU_LABEL}>Accounting</div>
              <button
                type="button"
                className="iv-status-option"
                disabled={busy}
                onClick={() => { setOpen(false); onToggleEntered(invoice); }}
                style={{ background: "#fff", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
              >
                <FeatherIcon icon={entered ? "rotate-ccw" : "book-open"} size={13} color="#4338CA" />
                <span style={{ color: "#4338CA", fontWeight: 700, fontSize: "0.78rem", flex: 1, textAlign: "left" }}>
                  {entered ? "Clear entered" : "Mark entered"}
                </span>
                {entered && <FeatherIcon icon="check" size={13} color="#059669" />}
              </button>
            </>
          )}

          {/* The manual override, deliberately last: it bypasses the workflow, so
              it should not compete with Approve for attention. It also moves ONE
              line while the actions above move the whole invoice, which the
              label and the note under it state outright. */}
          {canChangeStatus && (
            <>
              <div style={MENU_LABEL}>{multiItem ? "Set status · this item only" : "Set status"}</div>
              {multiItem && (
                <div style={{
                  padding: "0 12px 6px", maxWidth: 260,
                  fontSize: "0.66rem", color: "#64748B", lineHeight: 1.35,
                }}>
                  Changes {thisItem} only — Approve, Decline and Mark Paid act on the whole invoice.
                </div>
              )}
              {STATUS_VALUES.map((s) => {
                const sc = statusStyle(s);
                const active = s === invoice.status;
                return (
                  <button
                    key={s}
                    type="button"
                    className="iv-status-option"
                    onClick={() => choose(s)}
                    style={{ background: active ? sc.bg : "#fff" }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: sc.color, flexShrink: 0 }} />
                    <span style={{ color: sc.color, fontWeight: 700, fontSize: "0.78rem", flex: 1, textAlign: "left" }}>{s}</span>
                    {active && <FeatherIcon icon="check" size={13} color={sc.color} />}
                  </button>
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

const DocumentsPreviewModal = ({ isOpen, toggle, invoice }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [active, setActive] = useState(null);
  const keys = invoice?.attachments || [];

  useEffect(() => {
    if (!isOpen || keys.length === 0) {
      setSignedUrls({});
      setActive(null);
      return;
    }
    (async () => {
      const urls = await getInvoiceSignedUrls(keys);
      setSignedUrls(urls);
      setActive(keys[0]);
    })();
  }, [isOpen, invoice?._id]); // eslint-disable-line

  const activeUrl = active ? signedUrls[active] : null;
  const activeType = active ? getFileTypeFromKey(active) : "unknown";
  const activeName = active ? active.split("/").pop() : "Documents";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>
          <FeatherIcon icon="paperclip" size={15} style={{ marginRight: 6, verticalAlign: "middle" }} />
          {invoice?.title ? `${invoice.title} — Documents` : "Documents"}
        </span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {keys.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", color: "#94A3B8", gap: 10 }}>
            <FeatherIcon icon="file" size={36} />
            <div>No documents attached.</div>
          </div>
        ) : (
          <div style={{ display: "flex", height: "72vh" }}>
            <div style={{
              width: 96, flexShrink: 0, background: "#0B1220", padding: 10,
              display: "flex", flexDirection: "column", gap: 8, overflowY: "auto",
            }}>
              {keys.map((key) => {
                const t = getFileTypeFromKey(key);
                const url = signedUrls[key];
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActive(key)}
                    title={key.split("/").pop()}
                    style={{
                      width: "100%", height: 70, borderRadius: 8, overflow: "hidden",
                      border: isActive ? "2px solid #6366F1" : "2px solid transparent",
                      background: "#1E293B", cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", padding: 0,
                    }}
                  >
                    {t === "image" && url
                      ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <FeatherIcon icon={t === "pdf" ? "file-text" : "file"} size={22} color={t === "pdf" ? "#F87171" : "#94A3B8"} />}
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {!activeUrl ? (
                <Spinner style={{ color: "#A5B4FC" }} />
              ) : (
                <Previewer signedUrl={activeUrl} type={activeType} height="72vh" width="100%" />
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {activeUrl && (
          <a
            href={activeUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={activeName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

// Compact status summary chips for a vendor's grouped invoice (e.g. "2 Approved").
const GroupStatusSummary = ({ counts }) => (
  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
    {Object.entries(counts).map(([status, n]) => {
      const cfg = statusStyle(status);
      return (
        <span key={status} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
        }}>
          {n} {status}
        </span>
      );
    })}
  </div>
);

/**
 * How much of an invoice this band is actually showing.
 *
 * The list holds the line documents the caller's role filter and the list's own
 * filters returned, which can be part of a submission rather than all of it —
 * a manager only ever gets their own properties' items, and a status filter
 * returns only the lines in that status. The drawer asks the group endpoint for
 * `itemCount`/`isPartial`; the list cannot (that would be one request per row),
 * so it says what it honestly knows instead of presenting a slice's subtotal as
 * the invoice total.
 *
 * Two strengths, because they are two different claims: amber when items are
 * provably missing, plain grey when the view merely might be a slice — which for
 * a manager is every row, and would be noise in a warning colour.
 */
const GroupItemsChip = ({ group }) => {
  if (!group.isPartial) {
    return (
      <span style={{
        background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE",
        borderRadius: 12, padding: "1px 9px", fontSize: "0.7rem", fontWeight: 700,
      }}>
        {group.items.length} item{group.items.length !== 1 ? "s" : ""}
      </span>
    );
  }
  const known = group.missingItems > 0;
  return (
    <Tooltip title={group.partialTip} arrow>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: known ? "#FFFBEB" : "#F1F5F9",
        color: known ? "#D97706" : "#64748B",
        border: `1px solid ${known ? "#FDE68A" : "#E2E8F0"}`,
        borderRadius: 12, padding: "1px 9px", fontSize: "0.7rem", fontWeight: 700,
      }}>
        <FeatherIcon icon="eye-off" size={10} />
        {known
          ? `${group.items.length} of ${group.knownItemCount}+ items`
          : `${group.items.length} item${group.items.length !== 1 ? "s" : ""} shown`}
      </span>
    </Tooltip>
  );
};

// The band's money. Only a complete view may call it the invoice's total.
const GroupTotal = ({ group }) => {
  if (!group.isPartial) return <>{formatCurrency(group.totalSum)}</>;
  return (
    <Tooltip title={group.partialTip} arrow>
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.25 }}>
        <span>{formatCurrency(group.totalSum)}</span>
        <span style={{
          fontSize: "0.64rem", fontWeight: 700,
          color: group.missingItems > 0 ? "#D97706" : "#64748B",
        }}>
          subtotal of {group.items.length} item{group.items.length !== 1 ? "s" : ""}
        </span>
      </span>
    </Tooltip>
  );
};

const InvoicesTable = ({
  invoices: initialInvoices,
  loading,
  properties,
  vendors,
  userRole,
  userId = "",
  userLead = false,
  vendorEligibility = null,
  onRefresh,
  onFormOpen,
  filters,
}) => {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [formOpen, setFormOpen] = useState(false);

  // Vendors and their categories (with preset line descriptions) are loaded once
  // with the page; refresh them whenever the form opens so catalog edits made
  // since then — in another tab or by someone else — show up in the form.
  useEffect(() => {
    if (formOpen) onFormOpen?.();
  }, [formOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardInvoice, setForwardInvoice] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsInvoice, setDocsInvoice] = useState(null);
  // Approve / decline / mark-paid / entered-in-accounting each transition EVERY
  // line of the invoice, so the guard is per invoice, not per document: keyed on
  // one row it left the siblings clickable, and a second click hit the server
  // after the first had already moved that line — a red "only pending invoices
  // can be approved" for an action that in fact succeeded. A set rather than a
  // single key so one invoice finishing can never clear another's spinner.
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [restoreDraft, setRestoreDraft] = useState(false);
  const draftPromptRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  // Measured width of the sticky Actions column so the sticky Files column can
  // pin flush against its left edge (button count — and thus width — varies by role).
  const actionsThRef = useRef(null);
  const [actionsWidth, setActionsWidth] = useState(0);

  useEffect(() => {
    setInvoices(initialInvoices);
    setSelectedIds(new Set());
  }, [initialInvoices]);

  const role = (userRole || "").toLowerCase();
  const isCompany = role === "company";
  const isVendor = role === "vendor";
  const isAuditor = role === "auditor";
  const isManager = role === "manager";
  const isLead = role === "technical staff" && !!userLead;

  // Technical leads author invoices for their assigned properties too
  // (create / edit / forward); deletion is decided per invoice by canDeleteGroup.
  const canEdit = isCompany || isManager || isVendor || isLead;
  // Auditors may author an invoice but never revise one once it exists, so
  // creation is a wider right than editing. Enforced server-side too.
  const canCreate = canEdit || isAuditor;
  // Delete is per invoice, not per role: the creator (or company) may remove it
  // only while unapproved. Every visible line must pass, since the delete walks
  // them all. Server enforces the same rule.
  const canDeleteGroup = (group) =>
    group.items.length > 0 &&
    group.items.every((inv) => isInvoiceDeletable(inv, { id: userId, role }));
  const canApprove = isCompany || isLead;       // Pending → Approved / Declined
  const canMarkPaid = isCompany || isAuditor;    // Approved → Paid
  // Bulk selection: approvers tick Pending rows, payers tick Approved rows.
  const canBulkSelect = canApprove || canMarkPaid;
  const canSetEntered = isCompany || isAuditor;  // toggle entered-in-accounting
  const canChangeStatusDropdown = isCompany;     // full manual control

  // The action-button cluster lives on the master rows, which render whether or
  // not a group is expanded — so the column's width settles as soon as rows
  // exist. A stale width leaves the sticky Files column pinned inside Actions,
  // which then paints over it.
  useLayoutEffect(() => {
    if (isVendor || !canEdit) { setActionsWidth(0); return undefined; }
    const measure = () => {
      const w = actionsThRef.current?.getBoundingClientRect().width;
      if (w) setActionsWidth(w);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isVendor, canEdit, loading, invoices.length]);

  // Vendors generate invoices from their eligibility; internal roles pick the
  // property + building inside the modal (prefilled from the filter bar when set).
  const vendorCanCreate = isVendor && vendorEligibility?.canCreate;
  const canCreateNew = isVendor ? !!vendorCanCreate : canCreate;

  // Property list + vendor lock differ for vendor self-service.
  const formProperties = isVendor ? (vendorEligibility?.properties || []) : properties;
  const vendorName = vendorEligibility?.vendorName || "";
  const vendorCategoryName = vendorEligibility?.categoryName || "";
  const vendorCategories = vendorEligibility?.categories || [];

  const prefilledPropertyLabel = (() => {
    if (isVendor || !filters?.propertyId) return "";
    const prop = (properties || []).find((p) => p._id === filters.propertyId);
    return prop ? (prop.title || prop.name || filters.propertyId) : filters.propertyId;
  })();

  // Offer to restore an autosaved invoice draft (left behind by a session
  // timeout, crash or accidental navigation) once the user may create invoices.
  useEffect(() => {
    if (draftPromptRef.current || !canCreateNew) return;
    const draft = readInvoiceDraft();
    if (!draft?.form) return;
    draftPromptRef.current = true;
    Swal.fire({
      icon: "info",
      title: "Restore unsaved invoice?",
      html: "<p>We found an invoice you were working on but didn't get to submit. Restore it?</p>",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#DC2626",
      confirmButtonText: "Restore draft",
      cancelButtonText: "Discard",
    }).then((res) => {
      if (res.isConfirmed) {
        setSelected(null);
        setIsNew(true);
        setRestoreDraft(true);
        setFormOpen(true);
      } else {
        discardInvoiceDraft();
      }
    });
  }, [canCreateNew]);

  const openNew = () => {
    if (isVendor) {
      if (!vendorCanCreate) {
        toast.warning(vendorEligibility?.reason || "You are not set up to create invoices yet.");
        return;
      }
      if (!(vendorEligibility?.properties || []).length) {
        toast.warning("No properties are linked to your vendor profile yet.");
        return;
      }
    } else {
      if (!vendors || vendors.length === 0) {
        toast.warning("Add at least one vendor (Vendors tab) before creating an invoice.");
        return;
      }
    }
    setSelected(null);
    setIsNew(true);
    setFormOpen(true);
  };

  const openEdit = (invoice, e) => {
    e?.stopPropagation?.();
    // A settled invoice opens read-only rather than offering a form the server
    // would refuse to save.
    if (!isInvoiceEditable(invoice, role)) {
      openPreview(invoice);
      return;
    }
    setSelected(invoice);
    setIsNew(false);
    setFormOpen(true);
  };

  const openDetail = (invoice) => {
    setDrawerInvoice(invoice);
    setDrawerOpen(true);
  };

  // Auditors get a read-only full-form preview instead of the drawer.
  const openPreview = (invoice) => {
    setPreviewInvoice(invoice);
    setPreviewOpen(true);
  };

  // The master row's one files chip previews every document on the invoice.
  // The modal takes an invoice-shaped object, so hand it the first line wearing
  // the group's combined attachment list.
  const openGroupDocuments = (group, e) => {
    e?.stopPropagation?.();
    const base = group.items[0] || {};
    setDocsInvoice({ ...base, title: group.title || base.title, attachments: group.attachments });
    setDocsOpen(true);
  };

  const closeForm = (refresh = true) => {
    setFormOpen(false);
    setSelected(null);
    setRestoreDraft(false);
    if (refresh) onRefresh?.();
  };

  const openForward = (invoice, e) => {
    e.stopPropagation();
    setForwardInvoice(invoice);
    setForwardOpen(true);
  };

  const applyUpdated = (id, updated) => {
    setInvoices((prev) => prev.map((r) => (r._id === id ? updated : r)));
    if (drawerInvoice && drawerInvoice._id === id) setDrawerInvoice(updated);
  };

  const markBusy = (key, on) =>
    setBusyKeys((prev) => {
      const next = new Set(prev);
      on ? next.add(key) : next.delete(key);
      return next;
    });

  const isBusy = (inv) => busyKeys.has(groupKeyOf(inv));

  /**
   * Entered-in-accounting is stamped on every line of the invoice by the server,
   * so every row of the group has to move — patching only the clicked one leaves
   * the siblings showing no marker and still offering "Mark entered", and each
   * repeat click re-stamps the whole invoice and pushes another audit entry onto
   * every line of it.
   *
   * Local rows only: nothing here is written back, and rows this caller could
   * not read are not in the list to begin with.
   */
  const applyEnteredToGroup = (key, updated, entered) => {
    const patch = (r) => {
      if (updated && r._id === updated._id) return updated;
      if (groupKeyOf(r) !== key) return r;
      return entered
        ? { ...r, enteredAcctAt: updated?.enteredAcctAt || new Date().toISOString(), enteredAcctBy: updated?.enteredAcctBy }
        : { ...r, enteredAcctAt: null, enteredAcctBy: null };
    };
    setInvoices((prev) => prev.map(patch));
    setDrawerInvoice((prev) => (prev ? patch(prev) : prev));
  };

  const handleStatusChange = (id, updated) => {
    applyUpdated(id, updated);
    // The manual override moves this line alone, so the invoice can now hold a
    // split of statuses. Refetch so the band's counts show it instead of the
    // siblings rendering their pre-change state until a manual reload.
    onRefresh?.();
  };

  const handleApprove = async (invoice) => {
    const res = await Swal.fire({
      title: "Approve this invoice?",
      text: "The property manager will be notified and it moves to the auditors for payment.",
      input: "textarea",
      inputLabel: "Note (optional)",
      inputPlaceholder: "Add a note for the record…",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    const key = groupKeyOf(invoice);
    markBusy(key, true);
    try {
      const result = await approveInvoice(invoice._id, { note: (res.value || "").trim() });
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice approved");
      onRefresh?.();
    } catch { /* toast handled */ } finally { markBusy(key, false); }
  };

  const handleDecline = async (invoice) => {
    const res = await Swal.fire({
      title: "Decline this invoice?",
      text: "The vendor will be notified that the invoice was not approved.",
      input: "textarea",
      inputLabel: "Reason (required)",
      inputPlaceholder: "Tell the vendor why…",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Decline",
      confirmButtonColor: "#DC2626",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) => (!v?.trim() ? "A reason is required to decline" : undefined),
    });
    if (!res.isConfirmed) return;
    const key = groupKeyOf(invoice);
    markBusy(key, true);
    try {
      const result = await declineInvoice(invoice._id, { reason: (res.value || "").trim() });
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice declined");
      onRefresh?.();
    } catch { /* toast handled */ } finally { markBusy(key, false); }
  };

  const handleMarkPaid = async (invoice) => {
    const res = await Swal.fire({
      title: "Mark this invoice as paid?",
      text: "The vendor and property manager will be notified.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Mark Paid",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 440,
    });
    if (!res.isConfirmed) return;
    const key = groupKeyOf(invoice);
    markBusy(key, true);
    try {
      const result = await markInvoicePaid(invoice._id, {});
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice marked paid");
      onRefresh?.();
    } catch { /* toast handled */ } finally { markBusy(key, false); }
  };

  const handleToggleEntered = async (invoice) => {
    const next = !invoice.enteredAcctAt;
    if (!next) {
      const res = await Swal.fire({
        title: "Clear entered-in-accounting?",
        text: "This invoice will no longer be marked as entered in accounting.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Clear",
        confirmButtonColor: "#DC2626",
        cancelButtonText: "Cancel",
        width: 440,
      });
      if (!res.isConfirmed) return;
    }
    const key = groupKeyOf(invoice);
    markBusy(key, true);
    try {
      const result = await setInvoiceEnteredAcct(invoice._id, next);
      applyEnteredToGroup(key, result?.data, next);
      toast.success(next ? "Marked entered in accounting" : "Cleared entered-in-accounting");
      // The flag landed on every line, and an "Entered in Acct" filter decides
      // which of them still belong in the list — so re-read rather than trust
      // the local patch.
      onRefresh?.();
    } catch { /* toast handled */ } finally { markBusy(key, false); }
  };

  // The master-row checkbox selects the invoice: every visible line the bulk
  // actions could act on. The bulk endpoints still receive line ids, so their
  // semantics (and the server's skip logic) are untouched.
  const groupSelectableIds = (g) =>
    g.items
      .filter((inv) => (canApprove && inv.status === "Pending") || (canMarkPaid && inv.status === "Approved"))
      .map((inv) => inv._id);

  const toggleSelectGroup = (ids) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const all = ids.every((id) => next.has(id));
      ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
      return next;
    });

  const selectableIds = useMemo(
    () => (invoices || [])
      .filter((inv) => (canApprove && inv.status === "Pending") || (canMarkPaid && inv.status === "Approved"))
      .map((inv) => inv._id),
    [invoices, canApprove, canMarkPaid]
  );
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () =>
    setSelectedIds(() => (allSelectableSelected ? new Set() : new Set(selectableIds)));

  // Selected rows split by the bulk action that applies to them.
  const selection = useMemo(() => {
    const pending = { ids: [], total: 0 };
    const approved = { ids: [], total: 0 };
    for (const inv of invoices || []) {
      if (!selectedIds.has(inv._id)) continue;
      if (inv.status === "Pending") { pending.ids.push(inv._id); pending.total += payable(inv) || 0; }
      else if (inv.status === "Approved") { approved.ids.push(inv._id); approved.total += payable(inv) || 0; }
    }
    return { pending, approved, total: pending.total + approved.total };
  }, [invoices, selectedIds]);

  const handleBulkApprove = async () => {
    const ids = selection.pending.ids;
    if (ids.length === 0) return;
    const res = await Swal.fire({
      title: `Approve ${ids.length} invoice${ids.length !== 1 ? "s" : ""}?`,
      html: `<p>Total <strong>${formatCurrency(selection.pending.total)}</strong>.<br/><small style="color:#64748B">The property manager will be notified for each and they move to the auditors for payment.</small></p>`,
      input: "textarea",
      inputLabel: "Note (optional)",
      inputPlaceholder: "Add a note for the record…",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setBulkBusy(true);
    try {
      const result = await bulkApproveInvoices(ids, (res.value || "").trim());
      const approved = result?.data?.approved?.length || 0;
      const skipped = result?.data?.skipped?.length || 0;
      toast.success(`Approved ${approved}${skipped ? ` · ${skipped} skipped` : ""}`);
      onRefresh?.();
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to approve invoices");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkPaid = async () => {
    const ids = selection.approved.ids;
    if (ids.length === 0) return;
    const res = await Swal.fire({
      title: `Mark ${ids.length} invoice${ids.length !== 1 ? "s" : ""} as paid?`,
      html: `<p>Total <strong>${formatCurrency(selection.approved.total)}</strong>.<br/><small style="color:#64748B">The vendor and property manager will be notified for each.</small></p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Mark Paid",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setBulkBusy(true);
    try {
      const result = await bulkMarkInvoicesPaid(ids);
      const paid = result?.data?.paid?.length || 0;
      const skipped = result?.data?.skipped?.length || 0;
      toast.success(`Marked ${paid} paid${skipped ? ` · ${skipped} skipped` : ""}`);
      onRefresh?.();
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to mark invoices paid");
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Deletes the invoice as this list shows it. The server's delete endpoint is
   * per line (each call re-checks which S3 files the remaining lines still
   * reference), so the client walks the visible lines sequentially — parallel
   * calls would race that reference check. When the view is a slice, only the
   * visible lines are deleted and the confirm says so.
   */
  const handleDeleteGroup = async (group, e) => {
    e?.stopPropagation?.();
    const n = group.items.length;
    const name = group.title || group.rdLabel;
    const res = await Swal.fire({
      title: "Delete Invoice?",
      html: `<p>Delete invoice <strong>${name}</strong>${n > 1 ? ` (${n} items)` : ""}?<br/><small style="color:#64748B">${group.isPartial ? "Only the items visible in this list are deleted. " : ""}This cannot be undone.</small></p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      for (const inv of group.items) await deleteInvoice(inv._id);
      onRefresh?.();
    } catch {
      // deleteInvoice already surfaced the server's reason (not the creator /
      // already approved). A partial failure may have removed some lines — re-read.
      onRefresh?.();
    }
  };

  // Whether the rows this list holds can be PART of an invoice rather than all
  // of it. A manager's and a lead's list is scoped by the server to their
  // assigned properties, and these filters match individual line documents.
  // Year / month / category / vendor are identical on every line of a
  // submission, so they drop whole invoices and never split one.
  const scopeMayHideItems = isManager || role === "technical staff";
  const filterMayHideItems = !!(
    filters?.propertyId || filters?.buildingName || filters?.unitNo || filters?.entered || filters?.referenceNo ||
    (filters?.status && filters.status !== "all")
  );
  const sliceMayHideItems = scopeMayHideItems || filterMayHideItems;
  const sliceReason = scopeMayHideItems
    ? "You see only the items on properties assigned to you."
    : (filterMayHideItems ? "The current filters match individual line items, not whole invoices." : "");

  // A submission is still stored as one document per line item, so the list
  // groups them back into the invoice that was actually submitted. Every role
  // sees this now, not just vendors: an invoice is one invoice whoever looks.
  const groups = useMemo(() => {
    const map = new Map();
    (invoices || []).forEach((inv) => {
      // A pre-batching row has no groupId and is its own invoice. Keyed on _id
      // rather than on the number: two unrelated legacy records can share a
      // number, and keying on the number would merge them into one fake invoice.
      const key = groupKeyOf(inv);
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: inv.title,
          vendorName: inv.vendorName,
          createdAt: inv.createdAt,
          items: [],
        });
      }
      map.get(key).items.push(inv);
    });
    return Array.from(map.values()).map((g) => {
      // Entry order — persisted lineIndex where there is one, creation order
      // where there is not. Never the order the list happened to arrive in.
      const items = sortInvoiceItems(g.items);
      const totalSum = items.reduce((a, i) => a + (payable(i) || 0), 0);
      const statusCounts = items.reduce((acc, i) => {
        acc[i.status] = (acc[i.status] || 0) + 1;
        return acc;
      }, {});
      const overdueCount = items.filter(isOverdue).length;
      // Every document on the invoice, for the master row's one files chip.
      // De-duplicated: lines of one submission can share a staged file.
      const attachments = [...new Set(items.flatMap((i) => i.attachments || []))];
      // Every line of a submission now shares one number, so a group normally
      // has exactly one distinct value. A legacy submission drew a number per
      // line and keeps its range.
      const nos = [...new Set(items.map((i) => i.invoiceNo).filter((n) => n != null && n !== ""))];
      const rdLabel = nos.length === 1
        ? formatInvoiceNo({ invoiceNo: nos[0] })
        : (nos.length ? `RD ${Math.min(...nos)}–${Math.max(...nos)}` : "—");
      // The vendor's own invoice number is one invoice-level value that the
      // server copies onto every line. Legacy rows sometimes stored the RD
      // label itself here, which would just echo the chip beside it.
      const rdLabels = new Set(items.map((i) => formatInvoiceNo(i)));
      const referenceNo = items
        .map((i) => (i.referenceNo || "").trim())
        .find((r) => r && !rdLabels.has(r)) || "";
      // How much of the invoice this band is speaking for. `lineIndex` is 0-based
      // and dense within a submission, so the highest one on screen proves the
      // invoice has at least that many items — fewer rows than that and items are
      // missing for certain. Scope and filters can hide items without leaving
      // that gap, so they only prove the view MIGHT be partial. Legacy lines
      // carry no lineIndex and fall back to the second test alone.
      const indexes = items
        .map((i) => (typeof i.lineIndex === "number" ? i.lineIndex : null))
        .filter((n) => n != null);
      const knownItemCount = indexes.length ? Math.max(...indexes) + 1 : 0;
      const missingItems = Math.max(0, knownItemCount - items.length);
      const isPartial = missingItems > 0 || sliceMayHideItems;
      const partialTip = [
        missingItems > 0
          ? `This invoice has at least ${knownItemCount} items — ${missingItems} of them ${missingItems === 1 ? "is" : "are"} not in this list.`
          : "This invoice may have more items than the list is showing.",
        sliceReason,
        `The amount shown is the subtotal of the ${items.length} item${items.length !== 1 ? "s" : ""} here, not the invoice total.`,
      ].filter(Boolean).join(" ");
      return {
        ...g, items, totalSum, statusCounts, overdueCount, attachments, rdLabel,
        referenceNo, knownItemCount, missingItems, isPartial, partialTip,
      };
    });
  }, [invoices, sliceMayHideItems, sliceReason]);

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Columns the active header actually renders: the vendor table has its own
  // fixed 8-column set (its files chip moved onto the summary band), internal
  // roles have 10 or 11 depending on whether they can edit and whether they get
  // bulk-select.
  const colSpan = isVendor ? 8 : (canEdit ? 10 : 9) + (canBulkSelect ? 1 : 0);
  // The summary band renders the chevron, Total, Status and the pinned trailing
  // cell(s) itself; one wide label cell spans whatever is left. Derived from the
  // same expression as `colSpan` so the band lines up with BOTH column sets — a
  // hard-coded span silently misaligns the whole table for one of them.
  const bandFixedCells = isVendor
    ? 4                                                        // chevron + Total + Status + Actions
    : 4 + (canBulkSelect ? 1 : 0) + (canEdit ? 1 : 0);         // + bulk-select + pinned Actions
  const bandLabelSpan = Math.max(1, colSpan - bandFixedCells);
  // The Total row under an expanded group's items: one label cell right-aligned
  // against the amount column, then the amount, then the row's trailing cells
  // (status + the pinned column(s)). Derived from `colSpan` exactly like
  // `bandLabelSpan` so it lines up with BOTH column sets.
  const totalLabelSpan = isVendor ? colSpan - 3 : colSpan - 3 - (canEdit ? 1 : 0);

  const newButtonLabel = isVendor ? "Generate Invoice" : "New Invoice";
  const newButtonTip = isVendor
    ? (vendorCanCreate ? "" : (vendorEligibility?.reason || "You are not set up to create invoices yet."))
    : "";

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="file-text" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Finance</div>
              <div className="iv-header__title">{isVendor ? "My Invoices" : "Invoices"}</div>
            </div>
            {invoices.length > 0 && (
              <div className="iv-header__badges">
                {/* Invoices, not documents — the list groups a submission's line
                    items back into the one invoice they were submitted as. */}
                <span className="iv-header__badge iv-header__badge--records">
                  {groups.length} invoice{groups.length !== 1 ? "s" : ""}
                </span>
                {invoices.length !== groups.length && (
                  <span className="iv-header__badge iv-header__badge--records">
                    {invoices.length} line items
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="iv-header__actions">
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => setPayrollOpen(true)}
            >
              <FeatherIcon icon="calendar" size={14} />
              Payments Calendar
            </button>
            {(canCreate || isVendor) && (
              <Tooltip title={newButtonTip} arrow>
                <span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--primary"
                    onClick={openNew}
                    disabled={!canCreateNew}
                    style={{ opacity: canCreateNew ? 1 : 0.5, cursor: canCreateNew ? "pointer" : "not-allowed" }}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    {newButtonLabel}
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── Vendor grouped view ─────────────────────────────────────────── */}
        {isVendor ? (
          <SyncedScroll maxHeight="calc(100vh - 320px)">
            <table className="iv-tbl" style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  {/* The invoice number lives on the band now; the rows under it are items. */}
                  <th style={{ minWidth: 110, textAlign: "left", paddingLeft: 10 }}>Item</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                  <th style={{ minWidth: 90, textAlign: "center" }}>Unit</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Job</th>
                  <th style={{ minWidth: 110, textAlign: "right" }}>Total</th>
                  <th style={{ minWidth: 190, textAlign: "left", paddingLeft: 10 }}>Status</th>
                  <th className="iv-actions-th" style={{ width: 96 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
                )}
                {!loading && groups.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="iv-empty-cell">
                      <div className="iv-empty" style={{ minHeight: "25vh" }}>
                        <div className="iv-empty__icon-wrap"><FeatherIcon icon="file-text" size={24} color="#4F46E5" /></div>
                        <div className="iv-empty__title">No Invoices Yet</div>
                        <div className="iv-empty__sub">
                          {vendorCanCreate
                            ? 'Click "Generate Invoice" to submit your first invoice.'
                            : (vendorEligibility?.reason || "You are not set up to create invoices yet.")}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && groups.map((g) => {
                  const isOpen = expanded.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      {/* Submission summary band — the invoice's one actionable row.
                          Clicking it previews the whole invoice; the caret alone
                          expands the read-only item lines. */}
                      <tr
                        className="iv-row"
                        style={{ cursor: "pointer", background: "#F8FAFC", borderTop: "2px solid #E2E8F0" }}
                        onClick={() => openPreview(g.items[0])}
                      >
                        <td
                          style={{ textAlign: "center", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); toggleExpand(g.key); }}
                          title={isOpen ? "Collapse items" : "Expand items"}
                        >
                          <FeatherIcon icon={isOpen ? "chevron-down" : "chevron-right"} size={16} color="#4F46E5" />
                        </td>
                        <td colSpan={bandLabelSpan} style={{ textAlign: "left", paddingLeft: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, color: "#3730A3", fontSize: "0.84rem" }}>{g.rdLabel}</span>
                            <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{g.title || "Untitled"}</span>
                            <GroupItemsChip group={g} />
                            <span style={{ fontSize: "0.74rem", color: "#334155", fontWeight: 700 }}>
                              Created At: {g.createdAt ? moment(g.createdAt).format("MMM D, YYYY") : "—"}
                            </span>
                            {g.referenceNo && (
                              <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                                Ref {g.referenceNo}
                              </span>
                            )}
                            {g.attachments.length > 0 && (
                              <Tooltip title="Preview documents" arrow>
                                <button
                                  type="button" className="iv-chip iv-chip--indigo"
                                  style={{ border: "1px solid #C7D2FE", cursor: "pointer" }}
                                  onClick={(e) => openGroupDocuments(g, e)}
                                >
                                  <FeatherIcon icon="paperclip" size={11} />{g.attachments.length}
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 800, color: "#0F172A", fontSize: "0.86rem" }}>
                          <GroupTotal group={g} />
                        </td>
                        <td style={{ textAlign: "left", paddingLeft: 10 }}>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                            <GroupStatusSummary counts={g.statusCounts} />
                            {g.overdueCount > 0 && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
                                borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
                              }}>
                                <FeatherIcon icon="alert-triangle" size={10} /> {g.overdueCount} Overdue
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Matches the band's own tint so the pinned column has no
                            white gap. Vendors get edit + view + memos — no
                            forward or delete. */}
                        <td className="iv-actions-td" onClick={(e) => e.stopPropagation()} style={{ background: "#F8FAFC" }}>
                          <div className="iv-action-group">
                            {isInvoiceEditable(g.items[0], role) && (
                              <Tooltip title="Edit" arrow>
                                <button type="button" className="iv-ebtn" onClick={(e) => openEdit(g.items[0], e)}>
                                  <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip title="Preview invoice" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openPreview(g.items[0])}>
                                <FeatherIcon icon="eye" size={13} color="#0284C7" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Memos & activity" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openDetail(g.items[0])}>
                                <FeatherIcon icon="message-square" size={13} color="#4F46E5" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                      {isOpen && g.items.map((inv) => {
                        const ref = (Array.isArray(inv.properties) ? inv.properties : [])[0] || {};
                        // "2h 30m @ $40.00/hr" or "Flat $200.00" — how the line
                        // was priced, never a figure that invites a multiply.
                        const jobParts = [];
                        if (resolveRateType(inv) === "flat") {
                          jobParts.push(`Flat ${formatCurrency(flatAmountOf(inv))}`);
                        } else {
                          const h = Number(inv.hours) || 0;
                          const min = Number(inv.minutes) || 0;
                          jobParts.push(`${h}h${min ? ` ${min}m` : ""}`);
                          if (inv.amountPerHour !== "" && inv.amountPerHour != null) {
                            jobParts.push(`@ ${formatCurrency(Number(inv.amountPerHour) || 0)}/hr`);
                          }
                        }
                        if (Number(inv.materialCost)) {
                          jobParts.push(`+ ${formatCurrency(Number(inv.materialCost))} materials`);
                        }
                        return (
                          <tr key={inv._id} className="iv-row">
                            <td />
                            {/* The line's own description names it; a line without one
                                falls back to its number, from its ordinal — never from its
                                position in whatever slice this viewer can see. */}
                            <td style={{ textAlign: "left", paddingLeft: 10, maxWidth: 220 }}>
                              <div
                                style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={ref.jobDescription || ""}
                              >
                                {ref.jobDescription || itemLabel(inv) || formatInvoiceNo(inv)}
                              </div>
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.8rem" }}>{ref.title || "—"}</div>
                              <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{ref.buildingName || "—"}</div>
                            </td>
                            <td style={{ textAlign: "center", fontSize: "0.78rem", color: "#475569", fontWeight: 600 }}>
                              {ref.unitNo || "—"}
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontSize: "0.76rem", color: "#475569", fontWeight: 600 }}>{jobParts.join(" ")}</div>
                              <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{inv.category?.name || "—"}</div>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.82rem" }}>
                              {formatCurrency(payable(inv), inv.currency)}
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                                <StatusBadge status={inv.status} />
                                {isOverdue(inv) && <OverdueChip />}
                              </div>
                              {inv.status === "Declined" && inv.declineReason && (
                                <div style={{ fontSize: "0.68rem", color: "#B91C1C", marginTop: 3 }} title={inv.declineReason}>
                                  {inv.declineReason.length > 40 ? `${inv.declineReason.slice(0, 40)}…` : inv.declineReason}
                                </div>
                              )}
                            </td>
                            {/* Files and actions live on the summary band — the item
                                rows are read-only lines. */}
                            <td className="iv-actions-td" />
                          </tr>
                        );
                      })}
                      {/* Group total — right-aligned against the amount column. */}
                      {isOpen && (
                        <tr>
                          <td colSpan={totalLabelSpan} style={{
                            textAlign: "right", borderTop: "1px solid #E2E8F0",
                            fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em",
                            textTransform: "uppercase", color: "#64748B",
                          }}>
                            Total
                          </td>
                          <td style={{
                            textAlign: "right", borderTop: "1px solid #E2E8F0",
                            fontWeight: 800, color: "#0F172A", fontSize: "0.86rem",
                          }}>
                            {formatCurrency(g.totalSum)}
                          </td>
                          <td style={{ borderTop: "1px solid #E2E8F0" }} />
                          <td className="iv-actions-td" style={{ borderTop: "1px solid #E2E8F0" }} />
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </SyncedScroll>
        ) : (
          /* ── Internal roles: flat per-property table ────────────────────── */
          <>
          {canBulkSelect && selectedIds.size > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              margin: "0 0 12px", padding: "10px 16px", background: "#fff",
              border: "1px solid #E2E8F0", borderRadius: 10,
              boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
            }}>
              <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.82rem" }}>
                {selectedIds.size} selected
              </span>
              <span style={{ color: "#CBD5E1" }}>·</span>
              <span style={{ fontWeight: 800, color: "#059669", fontSize: "0.86rem" }}>
                {formatCurrency(selection.total)}
              </span>
              <div style={{ flex: 1 }} />
              {canApprove && selection.pending.ids.length > 0 && (
                <button
                  type="button" className="iv-btn iv-btn--primary"
                  disabled={bulkBusy} onClick={handleBulkApprove}
                  style={{ opacity: bulkBusy ? 0.6 : 1, cursor: bulkBusy ? "not-allowed" : "pointer" }}
                >
                  {bulkBusy
                    ? <Spinner size="sm" style={{ width: 13, height: 13 }} />
                    : <FeatherIcon icon="check-circle" size={14} />}
                  Approve selected
                </button>
              )}
              {canMarkPaid && selection.approved.ids.length > 0 && (
                <button
                  type="button" className="iv-btn iv-btn--primary"
                  disabled={bulkBusy} onClick={handleBulkPaid}
                  style={{ opacity: bulkBusy ? 0.6 : 1, cursor: bulkBusy ? "not-allowed" : "pointer" }}
                >
                  {bulkBusy
                    ? <Spinner size="sm" style={{ width: 13, height: 13 }} />
                    : <FeatherIcon icon="dollar-sign" size={14} />}
                  Mark selected Paid
                </button>
              )}
              <button
                type="button" className="iv-btn"
                disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}
                style={{ background: "#fff", border: "1px solid #E2E8F0", color: "#64748B" }}
              >
                Clear
              </button>
            </div>
          )}
          <SyncedScroll maxHeight="calc(100vh - 320px)">
            <table className="iv-tbl" style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  {canBulkSelect && (
                    <th style={{ width: 40, textAlign: "center" }}>
                      <StyledCheckbox
                        checked={allSelectableSelected}
                        disabled={selectableIds.length === 0}
                        onChange={toggleSelectAll}
                        label="Select all actionable invoices"
                      />
                    </th>
                  )}
                  <th style={{ width: 56 }}>S.No</th>
                  <th style={{ minWidth: 140 }}>Created At</th>
                  <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 10 }}>Invoice</th>
                  <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
                  <th style={{ minWidth: 130 }}>Category</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                  <th style={{ minWidth: 120, textAlign: "right" }}>Total</th>
                  <th style={{ minWidth: 200, textAlign: "center" }}>Status</th>
                  {isAuditor ? (
                    <th className="iv-actions-th" style={{ width: 140 }}>Actions</th>
                  ) : (
                    <th className="iv-files-th" style={{ minWidth: 70, right: canEdit ? actionsWidth : 0 }}>Files</th>
                  )}
                  {canEdit && <th ref={actionsThRef} className="iv-actions-th" style={{ width: 120 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
                )}

                {!loading && groups.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="iv-empty-cell">
                      <div className="iv-empty" style={{ minHeight: "25vh" }}>
                        <div className="iv-empty__icon-wrap"><FeatherIcon icon="file-text" size={24} color="#4F46E5" /></div>
                        <div className="iv-empty__title">No Invoices Yet</div>
                        <div className="iv-empty__sub">
                          {canEdit
                            ? 'Click "New Invoice" to record one.'
                            : "No invoices found for the current filters."}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && groups.map((g, gIdx) => {
                  const isOpen = expanded.has(g.key);
                  const selIds = canBulkSelect ? groupSelectableIds(g) : [];
                  return (
                    <Fragment key={g.key}>
                      {/* Submission summary band — one row per invoice, however many
                          line items it was submitted with. Clicking it previews the
                          whole invoice; the caret alone expands the item lines. */}
                      <tr
                        className="iv-row"
                        style={{ cursor: "pointer", background: "#F8FAFC", borderTop: "2px solid #E2E8F0" }}
                        onClick={() => openPreview(g.items[0])}
                      >
                        {canBulkSelect && (
                          <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                            {selIds.length > 0 ? (
                              <StyledCheckbox
                                checked={selIds.every((id) => selectedIds.has(id))}
                                onChange={() => toggleSelectGroup(selIds)}
                                label="Select invoice"
                              />
                            ) : null}
                          </td>
                        )}
                        <td
                          style={{ textAlign: "center", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); toggleExpand(g.key); }}
                          title={isOpen ? "Collapse items" : "Expand items"}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <FeatherIcon icon={isOpen ? "chevron-down" : "chevron-right"} size={16} color="#4F46E5" />
                            <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.82rem" }}>{gIdx + 1}</span>
                          </span>
                        </td>
                        <td colSpan={bandLabelSpan} style={{ textAlign: "left", paddingLeft: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, color: "#3730A3", fontSize: "0.84rem" }}>{g.rdLabel}</span>
                            <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{g.title || "Untitled"}</span>
                            <GroupItemsChip group={g} />
                            <span style={{ fontSize: "0.74rem", color: "#64748B", fontWeight: 600 }}>
                              {g.vendorName || "—"}
                            </span>
                            <span style={{ fontSize: "0.74rem", color: "#334155", fontWeight: 700 }}>
                              Created At: {g.createdAt ? moment(g.createdAt).format("MMM D, YYYY") : "—"}
                            </span>
                            {g.referenceNo && (
                              <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                                Ref {g.referenceNo}
                              </span>
                            )}
                            {/* Auditors have no Files column (Actions is pinned in its
                                place), so their one files chip rides the band label. */}
                            {isAuditor && g.attachments.length > 0 && (
                              <Tooltip title="Preview documents" arrow>
                                <button
                                  type="button" className="iv-chip iv-chip--indigo"
                                  style={{ border: "1px solid #C7D2FE", cursor: "pointer" }}
                                  onClick={(e) => openGroupDocuments(g, e)}
                                >
                                  <FeatherIcon icon="paperclip" size={11} />{g.attachments.length}
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 800, color: "#0F172A", fontSize: "0.86rem" }}>
                          <GroupTotal group={g} />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "center" }}>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
                              <GroupStatusSummary counts={g.statusCounts} />
                              {g.overdueCount > 0 && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
                                  borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
                                }}>
                                  <FeatherIcon icon="alert-triangle" size={10} /> {g.overdueCount} Overdue
                                </span>
                              )}
                            </div>
                            {/* Whole-invoice transitions live on the master row now.
                                Target the first line: approve / decline / mark-paid /
                                entered fan out to every line server-side. The manual
                                "Set status" override stays per-item and its menu says
                                so. Roles with no status rights just see the chips. */}
                            {(canApprove || canMarkPaid || canSetEntered || canChangeStatusDropdown) && (
                              <StatusControl
                                invoice={g.items[0]}
                                canChangeStatus={canChangeStatusDropdown}
                                canApprove={canApprove}
                                canMarkPaid={canMarkPaid}
                                canSetEntered={canSetEntered}
                                overdue={g.overdueCount > 0}
                                busy={isBusy(g.items[0])}
                                siblingCount={g.items.length - 1}
                                partialGroup={g.isPartial}
                                onStatusChange={handleStatusChange}
                                onApprove={handleApprove}
                                onDecline={handleDecline}
                                onMarkPaid={handleMarkPaid}
                                onToggleEntered={handleToggleEntered}
                              />
                            )}
                          </div>
                        </td>
                        {/* The pinned columns are opaque white by default — repeat the
                            band's tint so they leave no white gap over it. Files and
                            actions live here, once per invoice; the buttons act on
                            the whole invoice through its first line. */}
                        {isAuditor ? (
                          <td className="iv-actions-td" onClick={(e) => e.stopPropagation()} style={{ background: "#F8FAFC" }}>
                            <div className="iv-action-group">
                              <Tooltip title="Preview invoice" arrow>
                                <button type="button" className="iv-ebtn" onClick={() => openPreview(g.items[0])}>
                                  <FeatherIcon icon="eye" size={13} color="#0284C7" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Memos & activity" arrow>
                                <button type="button" className="iv-ebtn" onClick={() => openDetail(g.items[0])}>
                                  <FeatherIcon icon="message-square" size={13} color="#4F46E5" />
                                </button>
                              </Tooltip>
                              {canDeleteGroup(g) && (
                                <Tooltip title="Delete invoice" arrow>
                                  <button type="button" className="iv-dbtn" onClick={(e) => handleDeleteGroup(g, e)}>
                                    <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        ) : (
                          <td
                            className="iv-files-td"
                            onClick={(e) => e.stopPropagation()}
                            style={{ background: "#F8FAFC", textAlign: "center", right: canEdit ? actionsWidth : 0 }}
                          >
                            {g.attachments.length > 0 ? (
                              <Tooltip title="Preview documents" arrow>
                                <button
                                  type="button" className="iv-chip iv-chip--indigo"
                                  style={{ border: "1px solid #C7D2FE", cursor: "pointer" }}
                                  onClick={(e) => openGroupDocuments(g, e)}
                                >
                                  <FeatherIcon icon="paperclip" size={11} />{g.attachments.length}
                                </button>
                              </Tooltip>
                            ) : (<span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>)}
                          </td>
                        )}
                        {canEdit && (
                          <td className="iv-actions-td" onClick={(e) => e.stopPropagation()} style={{ background: "#F8FAFC" }}>
                            <div className="iv-action-group">
                              {isInvoiceEditable(g.items[0], role) && (
                                <Tooltip title="Edit" arrow>
                                  <button type="button" className="iv-ebtn" onClick={(e) => openEdit(g.items[0], e)}>
                                    <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                                  </button>
                                </Tooltip>
                              )}
                              <Tooltip title="Preview invoice" arrow>
                                <button type="button" className="iv-ebtn" onClick={() => openPreview(g.items[0])}>
                                  <FeatherIcon icon="eye" size={13} color="#0284C7" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Memos & activity" arrow>
                                <button type="button" className="iv-ebtn" onClick={() => openDetail(g.items[0])}>
                                  <FeatherIcon icon="message-square" size={13} color="#0284C7" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Forward" arrow>
                                <button type="button" className="iv-ebtn" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} onClick={(e) => openForward(g.items[0], e)}>
                                  <FeatherIcon icon="send" size={13} color="#059669" />
                                </button>
                              </Tooltip>
                              {canDeleteGroup(g) && (
                                <Tooltip title="Delete invoice" arrow>
                                  <button type="button" className="iv-dbtn" onClick={(e) => handleDeleteGroup(g, e)}>
                                    <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>

                      {isOpen && g.items.map((inv) => {
                        const categoryName = inv.category?.name || "—";
                        const propertyRefs = Array.isArray(inv.properties) ? inv.properties : [];

                        return (
                          <tr key={inv._id} className="iv-row">
                            {canBulkSelect && <td />}
                            <td />
                            <td style={{ textAlign: "center" }}>
                              <span style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 500 }}>{inv.createdAt ? moment(inv.createdAt).format("MMM D, YYYY") : "—"}</span>
                            </td>
                            {/* The invoice title, RD number and vendor reference sit on
                                the band, so the line is named by its own description and
                                unit. A line without them falls back to the title and its
                                number, from its ordinal — never from its position in
                                whatever slice this viewer can see. */}
                            <td style={{ textAlign: "left", paddingLeft: 10, maxWidth: 230 }}>
                              <div
                                style={{ fontSize: "0.84rem", fontWeight: 800, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={propertyRefs[0]?.jobDescription || inv.title || ""}
                              >
                                {propertyRefs[0]?.jobDescription || inv.title || "Untitled"}
                              </div>
                              <div style={{ fontSize: "0.72rem", color: "#64748B", fontWeight: 600, marginTop: 1 }}>
                                {propertyRefs[0]?.unitNo
                                  ? `Unit: ${propertyRefs[0].unitNo}`
                                  : itemLabel(inv) || formatInvoiceNo(inv)}
                              </div>
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{inv.vendorName || "—"}</div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span style={{
                                display: "inline-block", background: "#EEF2FF", color: "#4338CA",
                                border: "1px solid #C7D2FE", borderRadius: 6,
                                padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
                              }}>{categoryName}</span>
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              {propertyRefs.length === 0 ? (
                                <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>—</div>
                              ) : (
                                <>
                                  <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>{propertyRefs[0].title || "—"}</div>
                                  <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 1 }}>{propertyRefs[0].buildingName || "—"}</div>
                                </>
                              )}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                              <Tooltip arrow title={`Subtotal ${formatCurrency(inv.amount, inv.currency)} + GST ${inv.gstRate ?? 0}% (${formatCurrency(inv.gstAmount || 0, inv.currency)})`}>
                                <span>{formatCurrency(payable(inv), inv.currency)}</span>
                              </Tooltip>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {/* Display only: transitions live on the master row. */}
                              <div style={{ display: "inline-flex", flexDirection: "column", gap: 3, alignItems: "center" }}>
                                <StatusBadge status={inv.status} />
                                {isOverdue(inv) && <OverdueChip />}
                              </div>
                            </td>
                            {/* Files and actions live on the summary band — the item
                                rows are read-only lines. */}
                            {isAuditor ? (
                              <td className="iv-actions-td" />
                            ) : (
                              <td className="iv-files-td" style={{ right: canEdit ? actionsWidth : 0 }} />
                            )}
                            {canEdit && <td className="iv-actions-td" />}
                          </tr>
                        );
                      })}
                      {/* Group total — right-aligned against the amount column. */}
                      {isOpen && (
                        <tr>
                          <td colSpan={totalLabelSpan} style={{
                            textAlign: "right", borderTop: "1px solid #E2E8F0",
                            fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.05em",
                            textTransform: "uppercase", color: "#64748B",
                          }}>
                            Total
                          </td>
                          <td style={{
                            textAlign: "right", borderTop: "1px solid #E2E8F0",
                            fontWeight: 800, color: "#0F172A", fontSize: "0.86rem",
                          }}>
                            {formatCurrency(g.totalSum)}
                          </td>
                          <td style={{ borderTop: "1px solid #E2E8F0" }} />
                          {isAuditor ? (
                            <td className="iv-actions-td" style={{ borderTop: "1px solid #E2E8F0" }} />
                          ) : (
                            <td className="iv-files-td" style={{ borderTop: "1px solid #E2E8F0", right: canEdit ? actionsWidth : 0 }} />
                          )}
                          {canEdit && <td className="iv-actions-td" style={{ borderTop: "1px solid #E2E8F0" }} />}
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </SyncedScroll>
          </>
        )}
      </Paper>

      <PayrollCalendarModal
        isOpen={payrollOpen}
        onClose={() => setPayrollOpen(false)}
      />

      <InvoiceFormModal
        isOpen={formOpen}
        onClose={closeForm}
        invoiceData={selected}
        isNew={isNew}
        properties={formProperties}
        vendors={vendors}
        vendorLocked={isVendor}
        vendorName={vendorName}
        vendorCategoryName={vendorCategoryName}
        vendorCategories={vendorCategories}
        prefilledPropertyId={isVendor ? "" : (filters?.propertyId || "")}
        prefilledBuildingName={isVendor ? "" : (filters?.buildingName || "")}
        prefilledPropertyLabel={prefilledPropertyLabel}
        restoreDraft={restoreDraft}
      />

      {/* Read-only preview — every role reaches it from the master row's click
          or its eye button. */}
      <InvoiceFormModal
        isOpen={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewInvoice(null); }}
        invoiceData={previewInvoice}
        isNew={false}
        readOnly
        properties={formProperties}
        vendors={vendors}
      />

      <InvoiceDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerInvoice(null); }}
        invoice={drawerInvoice}
        canEdit={canEdit}
        canApprove={canApprove}
        canMarkPaid={canMarkPaid}
        canSetEntered={canSetEntered}
        busy={!!drawerInvoice && isBusy(drawerInvoice)}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onMarkPaid={handleMarkPaid}
        onChange={(updated) => {
          // Entered-in-accounting is stamped on every line of the invoice, so
          // when the drawer flips it the sibling rows have to follow — otherwise
          // they keep offering an action the server has already applied to them.
          const before = invoices.find((r) => r._id === updated._id);
          if (!!before?.enteredAcctAt !== !!updated.enteredAcctAt) {
            applyEnteredToGroup(groupKeyOf(updated), updated, !!updated.enteredAcctAt);
            return;
          }
          setDrawerInvoice(updated);
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
        }}
        onForward={() => { setForwardInvoice(drawerInvoice); setForwardOpen(true); }}
        onEdit={() => {
          // Close the drawer, then open the edit modal on the next tick so the
          // modal can never mount behind the drawer or race its unmount.
          const inv = drawerInvoice;
          setDrawerOpen(false);
          setDrawerInvoice(null);
          setTimeout(() => {
            setSelected(inv);
            setIsNew(false);
            setFormOpen(true);
          }, 0);
        }}
        onPreview={() => {
          const inv = drawerInvoice;
          setDrawerOpen(false);
          setDrawerInvoice(null);
          setTimeout(() => openPreview(inv), 0);
        }}
      />

      <InvoiceForwardModal
        isOpen={forwardOpen}
        onClose={() => { setForwardOpen(false); setForwardInvoice(null); }}
        invoice={forwardInvoice}
        onForwardSuccess={(updatedLog) => {
          if (!forwardInvoice) return;
          const updated = { ...forwardInvoice, communicationLog: updatedLog };
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
          if (drawerInvoice && drawerInvoice._id === updated._id) setDrawerInvoice(updated);
        }}
      />

      <DocumentsPreviewModal
        isOpen={docsOpen}
        toggle={() => { setDocsOpen(false); setDocsInvoice(null); }}
        invoice={docsInvoice}
      />
    </>
  );
};

export { InvoicesTable };
